import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../../api/axios';
import { useAppSelector } from '../../../store/hooks';

interface BacklogItem {
  _id: string;
  title?: string;
  name?: string;
  status: string;
  priority?: string;
  dueDate?: string;
  assignedTo?: any[];
  type: 'task' | 'feature' | 'bug';
  impact?: { severity: string };
  parentType?: 'project' | 'feature' | 'bug' | 'folder';
  parentId?: string;
  storyPoints?: number;
  approvalStatus?: string;
}

interface ParentInfo {
  _id: string;
  name: string;
}

interface GroupedSection {
  key: string;
  label: string;
  icon: 'feature' | 'folder' | 'unassigned';
  items: BacklogItem[];
}

interface Props {
  projectId: string;
}

const PRIORITY_ORDER: Record<string, number> = { critical: 4, high: 3, medium: 2, low: 1 };

const severityColors: Record<string, string> = {
  critical: 'bg-red-100 text-red-700',
  high: 'bg-orange-100 text-orange-700',
  medium: 'bg-yellow-100 text-yellow-700',
  low: 'bg-gray-100 text-gray-600',
};

const sortByPriority = (items: BacklogItem[]) =>
  [...items].sort((a, b) => {
    const pa = PRIORITY_ORDER[a.priority || a.impact?.severity || 'low'] ?? 0;
    const pb = PRIORITY_ORDER[b.priority || b.impact?.severity || 'low'] ?? 0;
    return pb - pa;
  });

const ChevronIcon: React.FC<{ expanded: boolean }> = ({ expanded }) => (
  <svg
    className={`w-4 h-4 transition-transform ${expanded ? 'rotate-90' : ''}`}
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
    strokeWidth={2}
  >
    <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
  </svg>
);

const FeatureIcon: React.FC = () => (
  <svg className="w-4 h-4 text-purple-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M14.25 6.087c0-.355.186-.676.401-.959.221-.29.349-.634.349-1.003 0-1.036-1.007-1.875-2.25-1.875s-2.25.84-2.25 1.875c0 .369.128.713.349 1.003.215.283.401.604.401.959v0a.64.64 0 01-.657.643 48.39 48.39 0 01-4.163-.3c.186 1.613.293 3.25.315 4.907a.656.656 0 01-.658.663v0c-.355 0-.676-.186-.959-.401a1.647 1.647 0 00-1.003-.349c-1.036 0-1.875 1.007-1.875 2.25s.84 2.25 1.875 2.25c.369 0 .713-.128 1.003-.349.283-.215.604-.401.959-.401v0c.31 0 .555.26.532.57a48.039 48.039 0 01-.642 5.056c1.518.19 3.058.309 4.616.354a.64.64 0 00.657-.643v0c0-.355-.186-.676-.401-.959a1.647 1.647 0 01-.349-1.003c0-1.035 1.008-1.875 2.25-1.875 1.243 0 2.25.84 2.25 1.875 0 .369-.128.713-.349 1.003-.215.283-.4.604-.4.959v0c0 .333.277.599.61.58a48.1 48.1 0 005.427-.63 48.05 48.05 0 00.582-4.717.532.532 0 00-.533-.57v0c-.355 0-.676.186-.959.401-.29.221-.634.349-1.003.349-1.035 0-1.875-1.007-1.875-2.25s.84-2.25 1.875-2.25c.37 0 .713.128 1.003.349.283.215.604.401.959.401v0a.656.656 0 00.658-.663 48.422 48.422 0 00-.37-5.36c-1.886.342-3.81.574-5.766.689a.578.578 0 01-.61-.58v0z" />
  </svg>
);

const FolderIcon: React.FC = () => (
  <svg className="w-4 h-4 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 12.75V12A2.25 2.25 0 014.5 9.75h15A2.25 2.25 0 0121.75 12v.75m-8.69-6.44l-2.12-2.12a1.5 1.5 0 00-1.061-.44H4.5A2.25 2.25 0 002.25 6v12a2.25 2.25 0 002.25 2.25h15A2.25 2.25 0 0021.75 18V9a2.25 2.25 0 00-2.25-2.25h-5.379a1.5 1.5 0 01-1.06-.44z" />
  </svg>
);

const UnassignedIcon: React.FC = () => (
  <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={1.5}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M20.25 7.5l-.625 10.632a2.25 2.25 0 01-2.247 2.118H6.622a2.25 2.25 0 01-2.247-2.118L3.75 7.5M10 11.25h4M3.375 7.5h17.25c.621 0 1.125-.504 1.125-1.125v-1.5c0-.621-.504-1.125-1.125-1.125H3.375c-.621 0-1.125.504-1.125 1.125v1.5c0 .621.504 1.125 1.125 1.125z" />
  </svg>
);

const BacklogView: React.FC<Props> = ({ projectId }) => {
  const navigate = useNavigate();
  const { sprints } = useAppSelector(s => s.sprints);
  const [tasks, setTasks] = useState<BacklogItem[]>([]);
  const [features, setFeatures] = useState<BacklogItem[]>([]);
  const [bugs, setBugs] = useState<BacklogItem[]>([]);
  const [featureMap, setFeatureMap] = useState<Record<string, string>>({});
  const [folderMap, setFolderMap] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeType, setActiveType] = useState<'all' | 'task' | 'feature' | 'bug'>('all');
  const [movingItem, setMovingItem] = useState<string | null>(null);
  const [targetSprint, setTargetSprint] = useState('');
  const [collapsedGroups, setCollapsedGroups] = useState<Set<string>>(new Set());

  const activeSprints = sprints.filter(s => s.status === 'active' || s.status === 'planned');

  useEffect(() => {
    setLoading(true);
    Promise.all([
      api.get(`/projects/${projectId}/backlog`),
      api.get(`/features/project/${projectId}`),
      api.get(`/folders/project/${projectId}`),
    ])
      .then(([backlogRes, featuresRes, foldersRes]) => {
        const data = backlogRes.data.data;
        setTasks((data.tasks || []).map((t: any) => ({ ...t, type: 'task' })));
        setFeatures((data.features || []).map((f: any) => ({ ...f, type: 'feature' })));
        setBugs((data.bugs || []).map((b: any) => ({ ...b, type: 'bug' })));

        // Build lookup maps for parent names
        const fMap: Record<string, string> = {};
        const allFeatures = featuresRes.data.data || featuresRes.data || [];
        (Array.isArray(allFeatures) ? allFeatures : []).forEach((f: ParentInfo) => {
          fMap[f._id] = f.name;
        });
        setFeatureMap(fMap);

        const flMap: Record<string, string> = {};
        const allFolders = foldersRes.data.data || foldersRes.data || [];
        (Array.isArray(allFolders) ? allFolders : []).forEach((f: ParentInfo) => {
          flMap[f._id] = f.name;
        });
        setFolderMap(flMap);
      })
      .catch(err => setError(err.response?.data?.message || 'Failed to load backlog'))
      .finally(() => setLoading(false));
  }, [projectId]);

  const toggleGroup = (key: string) => {
    setCollapsedGroups(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  // Group tasks by parent
  const taskGroups = useMemo((): GroupedSection[] => {
    const groups: Record<string, { label: string; icon: 'feature' | 'folder' | 'unassigned'; items: BacklogItem[] }> = {};

    tasks.forEach(task => {
      let key: string;
      let label: string;
      let icon: 'feature' | 'folder' | 'unassigned';

      if (task.parentType === 'feature' && task.parentId) {
        key = `feature:${task.parentId}`;
        label = `Feature: ${featureMap[task.parentId] || 'Unknown Feature'}`;
        icon = 'feature';
      } else if (task.parentType === 'folder' && task.parentId) {
        key = `folder:${task.parentId}`;
        label = `Folder: ${folderMap[task.parentId] || 'Unknown Folder'}`;
        icon = 'folder';
      } else {
        key = 'unassigned';
        label = 'Unassigned';
        icon = 'unassigned';
      }

      if (!groups[key]) groups[key] = { label, icon, items: [] };
      groups[key].items.push(task);
    });

    // Sort items within each group by priority
    return Object.entries(groups).map(([key, g]) => ({
      key,
      label: g.label,
      icon: g.icon,
      items: sortByPriority(g.items),
    })).sort((a, b) => {
      // Features first, then folders, then unassigned
      const order = { feature: 0, folder: 1, unassigned: 2 };
      return order[a.icon] - order[b.icon];
    });
  }, [tasks, featureMap, folderMap]);

  const backlogFeatures = useMemo(() => sortByPriority(features), [features]);
  const backlogBugs = useMemo(() => sortByPriority(bugs), [bugs]);

  const totalCount = tasks.length + features.length + bugs.length;
  const filteredTaskCount = tasks.length;

  const moveToSprint = async (item: BacklogItem) => {
    if (!targetSprint) return;
    try {
      await api.post(`/sprints/${targetSprint}/items`, {
        itemId: item._id,
        itemType: item.type,
      });
      if (item.type === 'task') setTasks(t => t.filter(x => x._id !== item._id));
      if (item.type === 'feature') setFeatures(f => f.filter(x => x._id !== item._id));
      if (item.type === 'bug') setBugs(b => b.filter(x => x._id !== item._id));
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to move item');
    }
    setMovingItem(null);
    setTargetSprint('');
  };

  const renderItemRow = (item: BacklogItem, indented: boolean = false) => {
    const label = item.title || item.name || 'Untitled';
    const priority = item.priority || item.impact?.severity;
    return (
      <div
        key={item._id}
        className={`bg-white border border-gray-200 rounded-lg px-4 py-3 flex items-center gap-3 hover:border-gray-300 transition-colors ${indented ? 'ml-6' : ''}`}
      >
        <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${typeBadge[item.type]}`}>
          {typeLabel[item.type]}
        </span>
        <span
          className="flex-1 text-sm font-medium text-gray-900 cursor-pointer hover:text-blue-600 truncate"
          onClick={() =>
            navigate(
              item.type === 'task'
                ? `/tasks/${item._id}`
                : item.type === 'bug'
                ? `/bugs/${item._id}`
                : `/features/${item._id}`
            )
          }
        >
          {label}
        </span>
        {priority && (
          <span className={`text-xs px-1.5 py-0.5 rounded ${severityColors[priority]}`}>
            {priority}
          </span>
        )}
        {item.storyPoints != null && (
          <span className="text-xs text-gray-500 bg-gray-100 px-1.5 py-0.5 rounded">
            {item.storyPoints} SP
          </span>
        )}
        {item.approvalStatus && (
          <span className="text-xs text-amber-600 bg-amber-50 px-1.5 py-0.5 rounded">
            {item.approvalStatus}
          </span>
        )}
        <span className="text-xs text-gray-400 capitalize">{item.status.replace('_', ' ')}</span>

        {/* Move to sprint */}
        {activeSprints.length > 0 &&
          (movingItem === item._id ? (
            <div className="flex items-center gap-1.5 shrink-0">
              <select
                value={targetSprint}
                onChange={e => setTargetSprint(e.target.value)}
                className="text-xs border border-gray-300 rounded px-2 py-1 focus:outline-none"
              >
                <option value="">Select sprint</option>
                {activeSprints.map(s => (
                  <option key={s._id} value={s._id}>
                    {s.name}
                  </option>
                ))}
              </select>
              <button
                onClick={() => moveToSprint(item)}
                disabled={!targetSprint}
                className="text-xs bg-blue-600 text-white px-2 py-1 rounded disabled:opacity-50"
              >
                Add
              </button>
              <button
                onClick={() => {
                  setMovingItem(null);
                  setTargetSprint('');
                }}
                className="text-xs text-gray-500"
              >
                ✕
              </button>
            </div>
          ) : (
            <button
              onClick={() => setMovingItem(item._id)}
              className="text-xs text-gray-500 border border-gray-200 px-2 py-1 rounded hover:border-blue-300 hover:text-blue-600 shrink-0"
            >
              → Sprint
            </button>
          ))}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex justify-center p-8">
        <div className="animate-spin h-8 w-8 border-4 border-blue-500 border-t-transparent rounded-full" />
      </div>
    );
  }

  const typeLabel: Record<string, string> = { task: 'Task', feature: 'Feature', bug: 'Bug' };
  const typeBadge: Record<string, string> = {
    task: 'bg-blue-100 text-blue-700',
    feature: 'bg-purple-100 text-purple-700',
    bug: 'bg-red-100 text-red-700',
  };

  const showTasks = activeType === 'all' || activeType === 'task';
  const showFeatures = activeType === 'all' || activeType === 'feature';
  const showBugs = activeType === 'all' || activeType === 'bug';
  const isEmpty =
    (showTasks ? tasks.length : 0) +
    (showFeatures ? features.length : 0) +
    (showBugs ? bugs.length : 0) === 0;

  return (
    <div className="space-y-4">
      {error && <div className="bg-red-50 text-red-700 px-4 py-2 rounded text-sm">{error}</div>}

      {/* Filters */}
      <div className="flex items-center gap-2 flex-wrap">
        {(['all', 'task', 'feature', 'bug'] as const).map(t => (
          <button
            key={t}
            onClick={() => setActiveType(t)}
            className={`px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
              activeType === t
                ? 'bg-blue-600 text-white'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            }`}
          >
            {t === 'all' ? 'All' : typeLabel[t]}
            <span className="ml-1.5 text-xs opacity-70">
              {t === 'all'
                ? totalCount
                : t === 'task'
                ? tasks.length
                : t === 'feature'
                ? features.length
                : bugs.length}
            </span>
          </button>
        ))}
      </div>

      {isEmpty ? (
        <div className="flex flex-col items-center justify-center min-h-[60vh] text-center px-8">
          <div className="w-20 h-20 rounded-2xl bg-green-50 flex items-center justify-center mb-6">
            <svg
              className="w-10 h-10 text-green-500"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              strokeWidth={1.5}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
          </div>
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Backlog is clear</h2>
          <p className="text-sm text-gray-500 max-w-sm">
            All work items are assigned to a sprint. Great job keeping things organized.
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {/* Grouped tasks */}
          {showTasks && taskGroups.length > 0 && taskGroups.map(group => {
            const expanded = !collapsedGroups.has(group.key);
            return (
              <div key={group.key}>
                <div
                  className="flex items-center gap-2 py-2 px-3 bg-gray-50 rounded-lg cursor-pointer hover:bg-gray-100 mb-1"
                  onClick={() => toggleGroup(group.key)}
                >
                  <ChevronIcon expanded={expanded} />
                  {group.icon === 'feature' && <FeatureIcon />}
                  {group.icon === 'folder' && <FolderIcon />}
                  {group.icon === 'unassigned' && <UnassignedIcon />}
                  <span className="text-sm font-semibold text-gray-800">{group.label}</span>
                  <span className="text-xs text-gray-500">
                    ({group.items.length} {group.items.length === 1 ? 'item' : 'items'})
                  </span>
                </div>
                {expanded && (
                  <div className="space-y-1.5">
                    {group.items.map(item => renderItemRow(item, true))}
                  </div>
                )}
              </div>
            );
          })}

          {/* Features section */}
          {showFeatures && backlogFeatures.length > 0 && (
            <div>
              <div className="flex items-center gap-2 my-3">
                <div className="flex-1 border-t border-gray-200" />
                <span className="text-xs font-semibold text-gray-400 uppercase tracking-wider">
                  Features (backlog)
                </span>
                <div className="flex-1 border-t border-gray-200" />
              </div>
              <div className="space-y-1.5">
                {backlogFeatures.map(item => renderItemRow(item))}
              </div>
            </div>
          )}

          {/* Bugs section */}
          {showBugs && backlogBugs.length > 0 && (
            <div>
              <div className="flex items-center gap-2 my-3">
                <div className="flex-1 border-t border-gray-200" />
                <span className="text-xs font-semibold text-gray-400 uppercase tracking-wider">
                  Bugs (backlog)
                </span>
                <div className="flex-1 border-t border-gray-200" />
              </div>
              <div className="space-y-1.5">
                {backlogBugs.map(item => renderItemRow(item))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default BacklogView;
